// src/utils/printLotLabels.js
// Generates a print-ready HTML page with Code 128 lot labels and triggers the browser print dialog.
// Each label includes the barcode, human-readable lot #, part info, qty, vendor, PO #, and received date.

import JsBarcode from 'jsbarcode';
import { toast } from 'react-toastify';

const escapeHtml = (s) =>
  String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const renderBarcodeSVG = (value) => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, value, {
    format: 'CODE128',
    displayValue: false,
    height: 50,
    width: 1.6,
    margin: 4,
    background: '#ffffff',
    lineColor: '#000000',
  });
  return new XMLSerializer().serializeToString(svg);
};

const renderLabelHtml = (label) => {
  let barcodeSvg = '';
  try {
    barcodeSvg = renderBarcodeSVG(label.lot_number);
  } catch (err) {
    console.error('Barcode render failed for', label.lot_number, err);
    barcodeSvg = `<div class="bc-error">Barcode failed</div>`;
  }

  return `
    <div class="lot-label">
      ${barcodeSvg}
      <div class="lot-num">${escapeHtml(label.lot_number)}</div>
      ${label.description ? `<div class="part">${escapeHtml(label.description)}</div>` : ''}
      <div class="meta">
        Qty: <strong>${escapeHtml(label.quantity)}</strong>${label.uom ? ` ${escapeHtml(label.uom)}` : ''}
      </div>
      <div class="meta">
        ${label.vendor_name ? escapeHtml(label.vendor_name) : ''}${label.po_number ? ` · PO ${escapeHtml(label.po_number)}` : ''}
      </div>
      ${label.received_date ? `<div class="meta date">Rec'd ${escapeHtml(label.received_date)}</div>` : ''}
    </div>
  `;
};

/**
 * Open a new window with one Code 128 label per entry and trigger print.
 * @param {Array<object>} labels  - each object has: lot_number, part_number, description, quantity, uom, vendor_name, po_number, received_date
 */
export const printLotLabels = (labels) => {
  if (!labels || labels.length === 0) {
    toast.error('No labels to print.');
    return;
  }

  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    toast.error('Pop-up was blocked. Allow pop-ups for this site to print labels.');
    return;
  }

  const labelsHtml = labels.map(renderLabelHtml).join('');

  win.document.open();
  win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Lot Labels (${labels.length})</title>
  <style>
    @page { margin: 0.25in; }
    html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #111; }
    .toolbar {
      position: sticky; top: 0; z-index: 10;
      padding: 10px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
      display: flex; gap: 8px; align-items: center;
    }
    .toolbar button {
      padding: 6px 12px; font-size: 13px; border: 1px solid #cbd5e1; background: white;
      border-radius: 3px; cursor: pointer;
    }
    .toolbar button.primary { background: #334155; color: white; border-color: #334155; }
    .toolbar .count { color: #64748b; font-size: 13px; margin-left: auto; }
    .sheet { padding: 12px; display: flex; flex-wrap: wrap; gap: 8px; }
    .lot-label {
      width: 2.5in; box-sizing: border-box; padding: 6pt 8pt;
      border: 1px dashed #cbd5e1; border-radius: 4px;
      text-align: center; page-break-inside: avoid; break-inside: avoid;
      background: white;
    }
    .lot-label svg { width: 100%; height: auto; max-height: 55pt; }
    .lot-num {
      font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
      font-size: 11pt; font-weight: 700; letter-spacing: 0.5px; margin-top: 2pt;
    }
    .part { font-size: 10pt; font-weight: 600; margin-top: 3pt; }
    .desc { font-size: 8pt; color: #475569; margin-top: 1pt; }
    .meta { font-size: 8pt; color: #334155; margin-top: 1pt; }
    .meta.date { color: #94a3b8; }
    .bc-error { color: red; font-size: 10pt; padding: 8pt; }
    @media print {
      .toolbar { display: none; }
      .lot-label { border: none; }
      .sheet { padding: 0; gap: 4pt; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <button class="primary" onclick="window.print()">Print</button>
    <button onclick="window.close()">Close</button>
    <span class="count">${labels.length} label${labels.length === 1 ? '' : 's'}</span>
  </div>
  <div class="sheet">${labelsHtml}</div>
  <script>
    window.addEventListener('load', () => setTimeout(() => window.print(), 250));
  </script>
</body>
</html>`);
  win.document.close();
};
